import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormRendererComponent } from '../components/form-renderer/form-renderer.component';
import { FormField } from '../models/form-builder/form-field.model';
import { ProgressIndicatorService } from './progress-indicator.service';
import { ChangedSelectionFieldValues } from '../models/flexible-selection-fields/changed-selection-field-values.model';
import { ListConstraintColumn } from '../models/flexible-selection-fields/list-constraint-column.model';
import { SelectionFieldValueChangeInstructions } from '../models/flexible-selection-fields/selection-field-value-change-instructions.model';
import { CollectApiServiceBase } from './collect-api-base.service';
import { FormInstanceElement } from '../models/form-builder/form-instance-element.model';
import { environment } from '../../../environments/environment';
import { FormInstance } from '../models/site-content/form-instance.model';
import { FieldIdToSelectedValues, FlexibleSelectionFieldInstructionsRequest } from '../models/flexible-selection-fields/flexible-selection-field-instructions-request.model';
import { FlexibleSelectionFieldType } from '../models/form-builder/form-field-types';
import { Subject, Observable } from "rxjs";
import { GridRowViewModel } from '../models/form-builder/grid-row.model';
import { FormFieldValidationResult } from '../models/flexible-selection-fields/form-field-validation-result.model';
import { TimerService } from './timer.service';
import { FlexibleSelectionFieldDefLogic } from '../models/form-builder/field-defs/flexible-selection-field-def-logic';
import { Form } from '../models/form-builder/form.model';

export interface FieldIdToSingleOption { [key: number]: string }

// Added for VNEXT-1645 - putting flex fields on SHP Objects that implement this get passed to Forms Grids
//
export interface FlexFieldInternalNameToValue { [fieldInternalName: string]: string[] /* array of strings which are the selected value(s) of the flex field */ }

@Injectable()
export class FlexibleSelectionFieldService extends CollectApiServiceBase<SelectionFieldValueChangeInstructions> {
    private static APPLY_FLEX_FIELD = 'Apply';
    private static CLEAR_FLEX_FIELD = 'Clear';

    // PJH - 1/14/2026 - VNEXT-2135 - added siteHomePageForm. Now that the SHP gets cached by use of CustomRouteReuseStrategy we need to cache its Form so
    // that clear and apply buttons on the SHP have the correct form to work with. This gets set by requestFlexibleFieldInstructionsForSiteHomePage() 
    private siteHomePageForm: Form = null;
    private formRenderer: FormRendererComponent;
    private cachedGridFieldOptions: ChangedSelectionFieldValues[];
    private autoSelectFieldValuesSubject: Subject<FieldIdToSingleOption> = new Subject<FieldIdToSingleOption>(); // allows changes to be pushed to subscribers
    private newOptionsSetForFieldSubject: Subject<number> = new Subject<number>();
    private shpFlexFieldButtonClicked: string;
    private flexFieldInternalNamesToValues: Subject<FlexFieldInternalNameToValue> = new Subject<FlexFieldInternalNameToValue>(); // allows changes to be pushed to subscribers;
    private autoSelectFieldValues: FieldIdToSingleOption = null; // allows proactive look ups

    // Pharvey - 11/25/2024 - selectedValuesByFieldId was introduced to provide a mechanism for GridRows' selected values to be set.
    // Form-level flex fields' selected values can be obtained by calling this.formInstance.getSelectedValuesByFieldId() but this doesn't
    // work for flex fields in grids -- there is no mechanism for one field in a grid row to get the value of another. And so selectedValuesByFieldId
    // was introduced. Each time a selection change is made in a grid, a mapping is added to this property and the whole mapping is sent to the server
    // so that the server can know the whole chain of selected values which it needs for correct behavior.
    // SIMPLIFY?: It might be worth converting form-level flex fields to use this same approach for consistency sake, and to enable the server
    // code to be simplified so it doesn't have multiple ways of find selected values
    private selectedValuesByFieldId: FieldIdToSelectedValues = {};

    constructor(http: HttpClient, progressIndicatorService: ProgressIndicatorService, private timerService: TimerService) {
        super(http, progressIndicatorService, environment.apiUrl, 'flexibleSelectionField', SelectionFieldValueChangeInstructions)
    }

    // Gets flex field instructions for an entire form instance
    public requestFlexibleFieldInstructionsForFormInstance(formInstance: FormInstance): Promise<SelectionFieldValueChangeInstructions> {
        this.selectedValuesByFieldId = {};
        let url = `${this.url}/${this.endpoint}/getFlexibleFieldInstructionsForFormInstance/${formInstance.id}`;

        return this.http.get<SelectionFieldValueChangeInstructions>(url)
            .toPromise()
            .then(instructions => {
                this.timerService.outputFor(TimerService.APPLY_FLEX_FIELD_OPTIONS, 'FlexFieldService.requestFlexibleFieldInstructionsForFormInstance() got instructions from AJAX call');
                this.updateTargetFields(instructions);
                return instructions;
            });
    }

    // Called when the value of a flex field changes. Get instructions for the updating the options of other flex fields
    // which depend on the one whose selection was changed
    public selectionFieldValueChanged(
        formField: FormField,
        formInstance: FormInstance,
        formInstanceElement: FormInstanceElement,
        parentValues: FormInstanceElement[],
        gridRowId: number = 0,
        gridColumnDefs: FormField[] = null): Promise<boolean> {

        // SIMPLIFY? Since we have the FormInstance do we need this reference to formRenderer? Couldn't the form instance answer this?
        if (this.formRenderer.hasDependentFields(formField)) {

            // Whenever a flex field's value is changed, we add a mapping of the field's Id to its new selected value so that the whole chain of selected values can be passed to the server
            this.updateSelectedValuesByFieldIds(formField, formInstance, formInstanceElement);
            //console.log('formField.dependsOnParentFormFieldId=' + formField.dependsOnParentFormFieldId);
            if (this.selectedValuesByFieldId[formField.dependsOnParentFormFieldId] === null) {
                //console.log(`Adding ${formInstance.getSelectedValuesByFieldId()[formField.dependsOnParentFormFieldId]}`);
                this.selectedValuesByFieldId[formField.dependsOnParentFormFieldId] = formInstance.getSelectedValuesByFieldId()[formField.dependsOnParentFormFieldId];
            }

            let payload = new FlexibleSelectionFieldInstructionsRequest(formInstance, formInstanceElement, this.selectedValuesByFieldId, null, gridRowId, gridColumnDefs);
            let url = `${this.url}/${this.endpoint}/GetFlexibleFieldInstructionsForField`;
            return this.http.post<SelectionFieldValueChangeInstructions>(url, payload)
                .toPromise()
                .then(instructions => {
                    this.updateTargetFields(instructions, gridColumnDefs);
                    let hasDependentGridFields = this.cachedGridFieldOptions?.length > 0;

                    let iAmNotAGridField = this.cachedGridFieldOptions?.filter(x => { return x.formFieldId == formField.id }).length == 0;
                    return iAmNotAGridField && hasDependentGridFields;
                });
        }
    }

    public validateGridRow(gridRow: GridRowViewModel, valuesToFieldIds: FieldIdToSelectedValues): Promise<FormFieldValidationResult[]> {
        let url = `${this.url}/${this.endpoint}/ValidateGridRow`;
        let payload = new FlexibleSelectionFieldInstructionsRequest(null, null, valuesToFieldIds, gridRow, gridRow.id, null);

        return this.http.post<FormFieldValidationResult[]>(url, payload)
            .toPromise()
            .then(validationResults => {
                let failures = validationResults.filter(x => x.validationFailureMessage);
                return failures;
            });
    }

    /* ---------------- BEGIN SITE HOME PAGE RELATED METHODS ------------------- */
    public setSiteHomePageForm(siteHomePageForm: Form): void {
        this.siteHomePageForm = siteHomePageForm;
    }

    // Gets flex field instructions for any flex fields on the Site Home Page
    public requestFlexibleFieldInstructionsForSiteHomePage(formId: number) {
        this.selectedValuesByFieldId = {};
        let url = `${this.url}/${this.endpoint}/getFlexibleFieldInstructionsForSiteHomePage/${formId}`;

        this.http.get<SelectionFieldValueChangeInstructions>(url)
            .toPromise()
            .then(instructions => {
                this.updateTargetFields(instructions, null);
            });
    }

    // Used by the Flex Field Button field which can be used on Site Home Pages
    // Loops all the flex fields on the form and creates a FlexFieldInternalNameToValue object from their values
    // which is then set as the next value of the flexFieldInternalNamesToValues Subject.
    public applyFlexFieldValuesOnSiteHomePage() {
        let namesToValues: FlexFieldInternalNameToValue = {};
        const valuesByFieldId: FieldIdToSelectedValues = this.formRenderer.SelectedValuesByFieldId;

        const allFlexFields = this.siteHomePageForm.formFields.filter(x => { return x.fieldDefinitionClassName == FlexibleSelectionFieldType });
        for (let field of allFlexFields) {
            const val = valuesByFieldId[field.id];
            if (val) {
                namesToValues[field.name] = val;
            }
        }
        this.shpFlexFieldButtonClicked = FlexibleSelectionFieldService.APPLY_FLEX_FIELD;
        this.flexFieldInternalNamesToValues.next(namesToValues);
    }

    /***
     * Used by the Flex Field Button field which is used on the Site Home Pages
     */
    public clearFlexFieldValuesOnSiteHomePage() {
        this.shpFlexFieldButtonClicked = FlexibleSelectionFieldService.CLEAR_FLEX_FIELD;
        this.flexFieldInternalNamesToValues.next({});
        const allFlexFields = this.siteHomePageForm.formFields.filter(x => { return x.fieldDefinitionClassName == FlexibleSelectionFieldType });
        for (const flexField of allFlexFields) {
            this.clearFlexFieldOptions(flexField, allFlexFields);
        }
    }

    public applyButtonWasClicked(): boolean {
        return this.shpFlexFieldButtonClicked == FlexibleSelectionFieldService.APPLY_FLEX_FIELD;
    }
    public clearButtonWasClicked(): boolean {
        return this.shpFlexFieldButtonClicked == FlexibleSelectionFieldService.CLEAR_FLEX_FIELD;
    }
    /* ---------------- END SITE HOME PAGE RELATED METHODS ------------------- */

    // Updates this.selectedValuesByFieldId and this.FormRenderer.SelectedValuesByFieldId
    // SIMPLIFY? this.FormInstance could have the one and only selectedValuesByFieldId which this component and form renderer could use as needed
    // whereas right now it's spread around and ripe to cause confusion
    private updateSelectedValuesByFieldIds(formField: FormField, formInstance: FormInstance, formInstanceElement: FormInstanceElement): void {
        // first, remove any fields that depend on formField (their values will no longer be relevant since their parent has changed)
        const formFieldsToRemove = this.findAllDependentsOfThisField(formField);
        for (let field of formFieldsToRemove) {
            this.clearFlexFieldOptions(field);
        }

        // next add the new value to the local hash and to FormRender's hash
        let value: string[] = null;
        if (formInstanceElement.childFormInstanceElements) {
            value = formInstanceElement.childFormInstanceElements.map(x => { return x.textValue; });
        } else {
            // This is bad - should be used IFieldDefinitionLogic - it gets the values of non-flex fields
            value = formInstanceElement.getValueAsStringArray();
        }

        // SIMPLIFY? This is confusing - why are we dealing with multiple hashes here and not just one?
        this.selectedValuesByFieldId[formInstanceElement.formFieldId] = value;
        this.FormRenderer.SelectedValuesByFieldId[formInstanceElement.formFieldId] = value;

        // Also make sure we have the values of any fields this field depends on
        if (formField.dependsOnParentFormFieldId && this.selectedValuesByFieldId[formField.dependsOnParentFormFieldId] == null) {
            this.selectedValuesByFieldId[formField.dependsOnParentFormFieldId] = formInstance.getSelectedValuesByFieldId()[formField.dependsOnParentFormFieldId];
        }
    }

    private clearFlexFieldOptions(flexField: FormField, allFlexFields: FormField[] = null) {
        delete this.selectedValuesByFieldId[flexField.id];
        delete this.formRenderer.SelectedValuesByFieldId[flexField.id];
        // flex field components subscribe to this and remove any selections for the referenced field when it changes
        this.NewOptionsSetForFieldSubject.next(flexField.id);

        if (flexField.dependsOnParentFormFieldId) {
            if (allFlexFields) {
                // only clear out the field's options if it depends on a flex field (rather than on a meta data field for example)
                const flexFieldIds = allFlexFields.map(x => { return x.id });
                if (flexFieldIds.indexOf(flexField.dependsOnParentFormFieldId) > -1)
                    flexField.setSelectOptions('');
            } else {
                flexField.setSelectOptions('');
            }
        }
    }

    // Recursively finds any form-level or grid-level fields which depend on formField
    private findAllDependentsOfThisField(formField: FormField, fieldsThatDependOnThisField: FormField[] = []): FormField[] {
        let formFields = this.FormRenderer.form.formFields;

        let formLevelDependentFields = formFields.filter(x => { return x.dependsOnParentFormFieldId == formField.id; });
        for (const f of formLevelDependentFields) {
            fieldsThatDependOnThisField.push(f);
        }

        let gridLevelDependentFields = [];
        for (const f of formFields) {
            for (const c of f.childFormFields) {
                if (c.dependsOnParentFormFieldId == formField.id) {
                    gridLevelDependentFields.push(c);
                    fieldsThatDependOnThisField.push(c);
                }
            }
        }

        if (formLevelDependentFields.length == 0 && gridLevelDependentFields.length == 0) {
            return fieldsThatDependOnThisField;
        } else {
            for (const d of formLevelDependentFields) {
                return this.findAllDependentsOfThisField(d, fieldsThatDependOnThisField);
            }
            for (const cd of gridLevelDependentFields) {
                return this.findAllDependentsOfThisField(cd, fieldsThatDependOnThisField);
            }
        }
    }

    // This method is called when a grid row is opened for editing (it is NOT called when flex fields in a row have their selections changed - selectionFieldValueChanged() is always called for that)
    // If gridsUseCachedInstructions is true, the method does NOT make a call to the server but rather uses instructions returned by a previous call to get instructions
    public getFlexibleSelectFieldInstructionsForGridRow(formInstanceId: number, rowData: any, gridColFieldDefs: FormField[], formInstance: FormInstance = null) {
        this.autoSelectFieldValues = {};
        let flexFields = gridColFieldDefs.filter(x => { return x.fieldDefinitionClassName == FlexibleSelectionFieldType })
        if (flexFields.length == 0) return;

        if (flexFields && rowData) {
            // When opening a gridrow for editing, need to build a mapping of that row's fields to their selected values in that row
            // That's what initializeSelectedValuesMappingForGridRow() does
            this.initializeSelectedValuesMappingForGridRow(flexFields, rowData, formInstance);
            let payload = new FlexibleSelectionFieldInstructionsRequest(formInstance, null, this.selectedValuesByFieldId, null /*gridRow*/, 0, gridColFieldDefs, true);

            let url = `${this.url}/${this.endpoint}/GetFlexibleFieldInstructionsForField`;
            this.http.post<SelectionFieldValueChangeInstructions>(url, payload)
                .toPromise()
                .then(instructions => {
                    this.updateTargetFields(instructions);
                    for (let newFieldValues of this.cachedGridFieldOptions) {
                        let formFieldId = newFieldValues.formFieldId;
                        let targetFormField: FormField = gridColFieldDefs.filter(x => { return x.id == formFieldId; })[0];
                        let options = newFieldValues.constraintValues.map(x => { return x.textValue; });
                        if (targetFormField) {
                            targetFormField.setSelectOptions(options.join('|'));
                            this.updateAutoSelectFieldValues(options, targetFormField);
                        }
                    }
                });
        } else {
            for (let newFieldValues of this.cachedGridFieldOptions) {
                let formFieldId = newFieldValues.formFieldId;
                let targetFormField: FormField = gridColFieldDefs.filter(x => { return x.id == formFieldId; })[0];
                if (targetFormField) {
                    let options = newFieldValues.constraintValues.map(x => { return x.textValue; });
                    targetFormField.setSelectOptions(options.join('|'));
                    //console.log('setting options for ' + targetFormField.name + ' to ' + options.join('|'));
                    this.updateAutoSelectFieldValues(options, targetFormField);
                }
            }
        }

        this.autoSelectFieldValuesSubject.next(this.autoSelectFieldValues);
    }

    // empties selectedValuesByFieldId and re-populates it from values in rowData
    private initializeSelectedValuesMappingForGridRow(flexFields: FormField[], rowData: any, formInstance: FormInstance) {
        this.selectedValuesByFieldId = {};
        for (let flexField of flexFields) {
            this.selectedValuesByFieldId[flexField.id] = rowData[flexField.id]?.split(FlexibleSelectionFieldDefLogic.MULTI_VALUE_SEPERATOR);

            if (this.flexFieldDependsOnFieldOutsideOfGrid(flexField, flexFields)) {
                this.selectedValuesByFieldId[flexField.dependsOnParentFormFieldId] = formInstance.getSelectedValuesByFieldId()[flexField.dependsOnParentFormFieldId];
            }
        }
    }

    private flexFieldDependsOnFieldOutsideOfGrid(flexField: FormField, flexFields: FormField[]) {
        return flexField.dependsOnParentFormFieldId && flexFields.map(x => { return x.id; }).indexOf(flexField.dependsOnParentFormFieldId) < 0;
    }

    // Takes a SelectionFieldValueChangeInstructions object and uses the formRenderer to update the options of all fields referenced in it
    // When the optional gridColumnDefs is passed and if a form-level target field cannot be found, it's searched for in gridColumnDefs
    // If no target field can be found and gridColumnDefs is not passed in, then the assumption is made that the selectable values are
    // are for a grid field and as added to this.cachedGridFieldOptions which can be accessed by Grids in a later call
    private updateTargetFields(instructions: SelectionFieldValueChangeInstructions, gridColumnDefs: FormField[] = null) {

        this.autoSelectFieldValues = {};
        for (let newFieldValues of instructions.fieldSelectableValues) {
            let formFieldId = newFieldValues.formFieldId;
            let targetFormField: FormField = this.formRenderer.form.formFields.find(x => { return x.id == formFieldId; });
            let options = newFieldValues.constraintValues.map(x => { return x.textValue; });
            if (targetFormField) {
                this.doUpdateTargetFields(targetFormField, options);
            } else if (gridColumnDefs) {
                let targetGridField = gridColumnDefs.filter(x => { return x.id == formFieldId; })[0];
                if (targetGridField) {
                    this.doUpdateTargetFields(targetGridField, options);
                }
            } else {
                if (this.cachedGridFieldOptions == null) {
                    this.cachedGridFieldOptions = [];
                }
                // if the field wasn't found it means its a field in a grid which is not exposed to formRenderer, so here we cache them ready for when a user
                // clicks on a grid row to edit it, at which point it will use these cached values to populate the options of any flex fields in it
                if (this.cachedGridFieldOptions.indexOf(newFieldValues) == -1) {
                    this.cachedGridFieldOptions.push(newFieldValues);
                }
            }
        }
        this.autoSelectFieldValuesSubject.next(this.autoSelectFieldValues);
    }

    private doUpdateTargetFields(targetField: FormField, options: string[]) {
        targetField.setSelectOptions(options.join('|'));
        this.updateAutoSelectFieldValues(options, targetField);
    }

    private updateAutoSelectFieldValues(options: string[], targetFormField: FormField) {
        if (options.length == 1) {
            this.autoSelectFieldValues[targetFormField.id] = options[0];
        }
    }

    // TODO - disambiguate this so it's clear there are two SelectedValuesByFieldId - one local to this service, and one for the whole FormInstance in FormRenderer
    public get SelectedValuesByFieldId(): FieldIdToSelectedValues {
        return this.formRenderer.SelectedValuesByFieldId;
    }

    // Allows Flex Field components to subscribe in case any need to auto-select a single value
    public get AutoSelectFieldValuesSubject(): Observable<any> {
        return this.autoSelectFieldValuesSubject.asObservable();
    }

    public get NewOptionsSetForFieldSubject(): any {
        return this.newOptionsSetForFieldSubject;
    }

    // Also provide a way to get values, not just subscribe to them
    // This is needed for fields in grids which don't get intialized
    // the same way as form level fields
    public get AutoSelectFieldValues(): any {
        return this.autoSelectFieldValues;
    }

    public get FlexFieldInternalNamesToValues(): Subject<FlexFieldInternalNameToValue> {
        return this.flexFieldInternalNamesToValues; //.asObservable();
    }

    // For the identifield ListValue constraint, returns its available columns
    public GetColumns(formFieldConstraintId: number): Promise<ListConstraintColumn[]> {
        let url = `${this.url}/${this.endpoint}/GetColumns/${formFieldConstraintId}`;
        return this.http.get<ListConstraintColumn[]>(url)
            .toPromise()
            .then(cols => {
                return cols;
            });
    }

    public SetDependsOnParentFormFieldId(formFieldId, dependsOnParentFormFieldId): Promise<FormField[]> {
        let url = `${this.url}/${this.endpoint}/SetDependsOnParentFormFieldId/${formFieldId}/${dependsOnParentFormFieldId}`;
        return this.http.get<FormField[]>(url)
            .toPromise()
            .then(ffs => {
                return ffs;
            });
    }

    public get FormRenderer(): FormRendererComponent {
        return this.formRenderer;
    }

    public set FormRenderer(value: FormRendererComponent) {
        this.formRenderer = value;
    }
}
